/**
 * `hardResetSession` — the comprehensive client-side teardown.
 *
 * Why this exists (in one paragraph):
 *   The shared `logout()` in `./auth.ts` handles the happy-path logout from
 *   an active session — every auth cookie is at `path=/` (the freshest
 *   write), and `clearAllSiteData` walks the current URL's path segments
 *   to delete them. That is sufficient when the user clicks "logout" from
 *   anywhere. It is NOT sufficient when re-establishing a session after
 *   an expiry, because legacy callsites in this codebase have left
 *   path-scoped residues at the URL the user was on when their session
 *   expired (typically the path in the `targetUrl` cookie). Those residues
 *   are invisible from `/login` (`document.cookie` filters by path on
 *   read) but the browser still sends them when the user later navigates
 *   back to that path — silently shadowing the fresh token (RFC 6265 §5.4).
 *   The result is the post-login `?reason=session-expired` loop, which
 *   the user could only break by clicking DevTools → Clear site data.
 *   This function is the programmatic equivalent, scoped to auth.
 *
 * Coverage:
 *   1. Every known auth cookie name purged at every parent path of the
 *      current URL AND every parent path of `targetUrl`. This is the
 *      H5 fix — invisible residues are deleted by issuing targeted
 *      writes (the browser honours name+path+domain matches even if the
 *      cookie was unreachable from `document.cookie`).
 *   2. The existing logout chain runs in full afterwards: visible cookies
 *      swept by `deleteAllCookies`, in-memory token + drafts +
 *      sessionStorage by `clearAllUserScoped`, TanStack Query cache by
 *      `queryClient.clear`, persisted Zustand blob by `persist.clearStorage`,
 *      Zustand state + caches + IDB + SW registrations by the auth store's
 *      `logout` action (which calls `clearAllSiteData` internally).
 *   3. Cross-tab broadcast so passive tabs end up in the same state.
 *
 * Idempotency / single-flight:
 *   N concurrent callers share one in-flight Promise — the request
 *   interceptor's 401 handler MUST collapse a 401 storm to one teardown.
 *   The promise is cleared in `.finally` so subsequent expiries can
 *   re-trigger a fresh reset.
 *
 * Scope discipline (NOT in this teardown):
 *   - Non-auth IndexedDB stores (e.g. glass-remnant) — the auth store's
 *     logout action already calls `clearAllSiteData`, which best-effort
 *     wipes all IDB databases. We do not add a second sweep.
 *   - Firebase Messaging SDK state — owned by the SDK, not auth.
 *   - User preferences (theme, language) — same behaviour as `logout()`.
 *     This function is a defensive equivalent, NOT a wider purge.
 */

import { useAuthStore } from '@/entities/auth/model'
import { broadcastLogout, clearAllUserScoped } from '@/shared/storage'
import { deleteAllCookies, deleteCookieAtAllPaths, getCookie } from '@/shared/lib/formatter'
import { queryClient } from './queryClient'

/**
 * Cookie names written by the active `login()` flow plus historical
 * callsites. Walked explicitly at every plausible path because
 * `document.cookie` cannot enumerate path-scoped residues from the
 * current URL when the residue's path is not a prefix of the URL.
 */
const KNOWN_AUTH_COOKIE_NAMES: readonly string[] = [
  'token',
  'expiresAt',
  'refreshToken',
  'fullName',
  'userId',
  'username',
  'targetUrl',
]

let resetInFlight: Promise<void> | null = null

export function hardResetSession(): Promise<void> {
  if (resetInFlight) return resetInFlight
  resetInFlight = doHardReset().finally(() => {
    resetInFlight = null
  })
  return resetInFlight
}

async function doHardReset(): Promise<void> {
  // Snapshot `targetUrl` BEFORE the purge — once we delete it, we can no
  // longer recover the path the user was on when they were bounced.
  const targetUrl = typeof window !== 'undefined' ? (getCookie('targetUrl') ?? '') : ''
  const extraPaths = targetUrl ? [targetUrl] : []

  // Phase 1 — explicit known-name purge at every plausible path. This is
  // the H5 fix: residues set by legacy raw `document.cookie =` writes are
  // invisible from `document.cookie` when the current URL's path does not
  // match, but the browser will still honour a delete write targeting the
  // residue's exact path. We don't know the residue's path; we issue
  // deletes at every parent of every URL we know about.
  for (const name of KNOWN_AUTH_COOKIE_NAMES) {
    deleteCookieAtAllPaths(name, extraPaths)
  }

  // Phase 2 — existing logout cleanup chain. Matches the order in
  // `src/shared/api/auth.ts:logout()` so callers can swap freely.
  deleteAllCookies()
  clearAllUserScoped()
  queryClient.clear()

  // The persisted Zustand blob only stores `sideBar`, but clearing it
  // matches the "hard reset" intent — and `useAuthStore.getState().logout()`
  // below would otherwise leave the persisted layer holding a stale
  // sideBar state across the boundary. Wrapped in try because
  // `persist.clearStorage` rejects silently in SSR/test environments.
  try {
    useAuthStore.persist?.clearStorage()
  } catch {
    // non-fatal — persist may not be initialised
  }

  useAuthStore.getState().logout()
  broadcastLogout()
}
